import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  CardContent,
  Typography,
  Button,
  Dialog,
  IconButton,
  Box,
  Grid,
  TextField,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { motion, AnimatePresence } from "framer-motion";
import styled from "styled-components";
import { mask as masker, unMask } from "remask";
import { api } from "@api/api";
import DialogRecusaCadastro from "./dialogRecusaCadastro";
import FeedbackInline from "@componentes/feedback/FeedbackInline";
import {
  ajustarTamanhoFonteAnalista,
  tamanhosGlobais,
} from "../../../../estilos/global";

const colors = {
  primary: "#1976d2",
  success: "#4caf50",
  error: "#f44336",
  warning: "#ff9800",
  background: "#f5f5f5",
  text: "#333333",
  accent: "#6c5ce7",
};

const MASKS = {
  cep: ["99999-999"],
  telefone: ["(99) 9999-9999", "(99) 99999-9999"],
  cnpj: ["99.999.999/9999-99"],
  numero: ["9999999999"],
};

const applyMask = (value, mask) => {
  return masker(value, mask);
};

const StyledCard = styled(motion.div)`
  max-width: 100%;
  margin: 0;
  padding: 16px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;

  display: grid;
  grid-template-columns: 40px minmax(0, 1fr);
  column-gap: 12px;
  align-items: start;

  > .MuiIconButton-root {
    margin-bottom: 0;
  }

  > .MuiCardContent-root,
  > .MuiCardContent-root:last-child {
    min-width: 0;
    padding: 0;
  }

  .MuiGrid-container {
    margin-top: 0;
  }

  .MuiGrid-container > .MuiGrid-item {
    padding-top: 8px;
  }

  .MuiTypography-body1 {
    font-size: ${ajustarTamanhoFonteAnalista("0.875rem")};
  }

  .MuiTypography-h6 {
    font-size: ${ajustarTamanhoFonteAnalista("1rem")};
  }

  .MuiTypography-body2 {
    font-size: ${ajustarTamanhoFonteAnalista("0.8125rem")};
  }

  @media (min-width: 900px) {
    flex: 1 1 auto;
    min-height: min-content;

    > .MuiCardContent-root {
      align-self: stretch;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      min-height: min-content;
    }

    > .MuiCardContent-root > h3 {
      align-self: flex-start;
    }

    .MuiTypography-body1 {
      font-size: ${ajustarTamanhoFonteAnalista(tamanhosGlobais.textoPadrao)};
    }

    button:not(.MuiIconButton-root) {
      font-size: ${ajustarTamanhoFonteAnalista(tamanhosGlobais.botao)};
    }

    .MuiTypography-h6 {
      font-size: ${ajustarTamanhoFonteAnalista(tamanhosGlobais.tituloSecao)};
    }

    .MuiTypography-body2 {
      font-size: ${ajustarTamanhoFonteAnalista(tamanhosGlobais.textoAuxiliar)};
    }
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 8px;
    background: ${colors.primary};
  }
`;

const StyledButton = styled(motion.button)`
  margin: 0;
  padding: 8px 20px;
  min-height: 36px;
  min-width: 120px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-size: ${ajustarTamanhoFonteAnalista("0.875rem")};
  font-weight: 600;
  color: white;
  background: ${(props) => props.color || colors.primary};
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  text-transform: uppercase;
  letter-spacing: 1px;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.2);
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }

  &:hover::after {
    transform: translateX(0);
  }
`;

const InfoItem = styled(motion.div)`
  padding: 6px 10px;
  margin-bottom: 4px;
  background: rgba(245, 245, 245, 0.5);
  border-radius: 8px;
  border-left: 4px solid ${colors.accent};

  strong {
    color: ${colors.accent};
  }
`;

const DocumentPreview = styled(motion.div)`
  margin-top: 12px;
  padding: 12px;
  background: rgba(25, 118, 210, 0.05);
  border-radius: 12px;
  border: 2px dashed rgba(25, 118, 210, 0.3);
  text-align: center;
  cursor: pointer;
`;

const EdicaoInfo = ({ dados, onSave, onClose }) => {
  const [formData, setFormData] = useState(dados);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let maskedValue = value;

    if (name === "cep") {
      maskedValue = applyMask(unMask(value), MASKS.cep);
    } else if (name === "contato") {
      maskedValue = applyMask(unMask(value), MASKS.telefone);
    } else if (name === "cnpj") {
      maskedValue = applyMask(unMask(value), MASKS.cnpj);
    } else if (name === "numero") {
      maskedValue = applyMask(unMask(value), MASKS.numero);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: maskedValue || value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const dataToSave = {
      ...formData,
      cep: unMask(formData.cep),
      contato: unMask(formData.contato),
      ...(formData.cnpj && { cnpj: unMask(formData.cnpj) }),
    };

    onSave(dataToSave);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{ mb: 3, color: colors.primary }}
      >
        Editar Informações
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Nome"
            name="nome"
            value={formData.nome || ""}
            onChange={handleChange}
            margin="normal"
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email || ""}
            onChange={handleChange}
            margin="normal"
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Contato"
            name="contato"
            value={formData.contato || ""}
            onChange={handleChange}
            margin="normal"
            variant="outlined"
            inputProps={{
              maxLength: 15,
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Modalidade"
            name="modalidade"
            value={formData.modalidade || ""}
            onChange={handleChange}
            margin="normal"
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Município"
            name="municipio"
            value={formData.municipio || ""}
            onChange={handleChange}
            margin="normal"
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Rua"
            name="rua"
            value={formData.rua || ""}
            onChange={handleChange}
            margin="normal"
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Número"
            name="numero"
            value={formData.numero || ""}
            onChange={handleChange}
            margin="normal"
            variant="outlined"
            inputProps={{
              maxLength: 10,
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Bairro"
            name="bairro"
            value={formData.bairro || ""}
            onChange={handleChange}
            margin="normal"
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="CEP"
            name="cep"
            value={formData.cep || ""}
            onChange={handleChange}
            margin="normal"
            variant="outlined"
            inputProps={{
              maxLength: 9,
            }}
          />
        </Grid>

        {dados.cnpj && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="CNPJ"
              name="cnpj"
              value={formData.cnpj || ""}
              onChange={handleChange}
              margin="normal"
              variant="outlined"
              inputProps={{
                maxLength: 18,
              }}
            />
          </Grid>
        )}
      </Grid>

      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          variant="contained"
          color="error"
          onClick={onClose}
          sx={{ borderRadius: "12px", px: 3 }}
        >
          Cancelar
        </Button>

        <Button
          type="submit"
          variant="contained"
          color="primary"
          sx={{ borderRadius: "12px", px: 3 }}
        >
          Salvar
        </Button>
      </Box>
    </Box>
  );
};

const EscolaInfo = () => {
  const { tipo, id } = useParams();
  const navigate = useNavigate();

  const [dados, setDados] = useState(null);
  const [documentos, setDocumento] = useState(null);
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [recusaAberta, setRecusaAberta] = useState(false);
  const [erroDecisao, setErroDecisao] = useState("");
  const [aprovando, setAprovando] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await fetch(api.instData(tipo, id), {
        credentials: "include",
      });

      const data = await response.json();

      const maskedData = {
        ...data,
        contato: data.contato ? applyMask(data.contato, MASKS.telefone) : "",
        cep: data.cep ? applyMask(data.cep, MASKS.cep) : "",
        ...(data.cnpj && { cnpj: applyMask(data.cnpj, MASKS.cnpj) }),
      };

      setDados(maskedData);

      if (data.documentos) {
        const byteCharacters = atob(data.documentos);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });

        setDocumento(URL.createObjectURL(blob));
      }
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
    } finally {
      setIsLoading(false);
    }
  }, [tipo, id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAprovar = useCallback(async () => {
    if (aprovando) return;

    setAprovando(true);
    setErroDecisao("");

    try {
      const response = await fetch(api.approve(tipo, id), {
        method: "PATCH",
        credentials: "include",
      });

      if (!response.ok) {
        const dados = await response.json().catch(() => ({}));

        throw new Error(
          dados.message || "Não foi possível aprovar o cadastro.",
        );
      }

      navigate(-1);
    } catch (error) {
      setErroDecisao(
        error instanceof Error ? error.message : "Erro ao aprovar cadastro.",
      );
    } finally {
      setAprovando(false);
    }
  }, [tipo, id, navigate, aprovando]);

  const handleRecusar = useCallback(() => {
    setErroDecisao("");
    setRecusaAberta(true);
  }, []);

  const handleEditar = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleSave = useCallback(
    async (updatedData) => {
      try {
        await fetch(api.edit(tipo, id), {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedData),
          credentials: "include",
        });

        setDados(updatedData);
        setIsEditing(false);
      } catch (error) {
        console.error("Erro ao salvar edição:", error);
      }
    },
    [tipo, id],
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: colors.background,
        }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          style={{
            width: 50,
            height: 50,
            border: `5px solid ${colors.primary}`,
            borderTopColor: "transparent",
            borderRadius: "50%",
          }}
        />
      </Box>
    );
  }

  if (!dados) {
    return (
      <Box
        sx={{
          backgroundColor: colors.background,
          minHeight: "100vh",
          padding: "2rem",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" color={colors.error}>
          Ocorreu um erro ao carregar os dados.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: colors.background,
        minHeight: 0,
        padding: 0,
        position: "relative",
        display: { xs: "block", md: "flex" },
        flexDirection: "column",
        flex: "1 1 auto",
      }}
    >
      <StyledCard
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            mb: 2,
            background: colors.primary,
            color: "white",
            "&:hover": {
              background: colors.primary,
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        <CardContent>
          <FeedbackInline mensagem={erroDecisao} />

          <Typography
            variant="h5"
            component="h3"
            gutterBottom
            sx={{
              color: colors.text,
              fontWeight: "bold",
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              display: "inline-block",
            }}
          >
            {dados.nome}
          </Typography>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <InfoItem>
                  <Typography variant="body1" sx={{ color: colors.text }}>
                    <strong>Tipo:</strong> Escola
                  </Typography>
                </InfoItem>

                <InfoItem>
                  <Typography variant="body1" sx={{ color: colors.text }}>
                    <strong>Email:</strong> {dados.email}
                  </Typography>
                </InfoItem>

                <InfoItem>
                  <Typography variant="body1" sx={{ color: colors.text }}>
                    <strong>Contato:</strong> {dados.contato}
                  </Typography>
                </InfoItem>

                <InfoItem>
                  <Typography variant="body1" sx={{ color: colors.text }}>
                    <strong>Modalidade:</strong> {dados.modalidade}
                  </Typography>
                </InfoItem>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <InfoItem>
                  <Typography variant="body1" sx={{ color: colors.text }}>
                    <strong>Município:</strong> {dados.municipio}
                  </Typography>
                </InfoItem>

                <InfoItem>
                  <Typography variant="body1" sx={{ color: colors.text }}>
                    <strong>Endereço:</strong> {dados.rua}, {dados.numero}
                  </Typography>
                </InfoItem>

                <InfoItem>
                  <Typography variant="body1" sx={{ color: colors.text }}>
                    <strong>Bairro:</strong> {dados.bairro}, CEP: {dados.cep}
                  </Typography>
                </InfoItem>

                {tipo === "mantenedora" && (
                  <InfoItem>
                    <Typography variant="body1" sx={{ color: colors.text }}>
                      <strong>CNPJ:</strong> {dados.cnpj}
                    </Typography>
                  </InfoItem>
                )}
              </motion.div>
            </Grid>
          </Grid>

          {documentos && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <DocumentPreview
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setOpen(true)}
              >
                <Typography variant="h6" sx={{ color: colors.primary, mb: 1 }}>
                  Documento Disponível
                </Typography>

                <Typography variant="body2" sx={{ color: colors.text }}>
                  Clique para visualizar o documento anexado
                </Typography>
              </DocumentPreview>
            </motion.div>
          )}

          <Box
            sx={{
              mt: 3,
              display: "flex",
              gap: "1rem",
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <StyledButton
              color={colors.success}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={aprovando}
              onClick={handleAprovar}
            >
              Aprovar
            </StyledButton>

            <StyledButton
              color={colors.error}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={aprovando}
              onClick={handleRecusar}
            >
              Recusar
            </StyledButton>

            <StyledButton
              color={colors.warning}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={aprovando}
              onClick={handleEditar}
            >
              Editar
            </StyledButton>
          </Box>
        </CardContent>

        <DialogRecusaCadastro
          open={recusaAberta}
          tipo={tipo}
          cadastroId={id}
          onClose={() => setRecusaAberta(false)}
          onRecusado={() => navigate(-1)}
        />

        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: {
              borderRadius: "16px",
              overflow: "hidden",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "8px",
              background: colors.primary,
            }}
          >
            <IconButton onClick={() => setOpen(false)} sx={{ color: "white" }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {documentos && (
            <iframe
              src={documentos}
              width="100%"
              height="600px"
              title="documentos"
              style={{ border: "none" }}
            />
          )}
        </Dialog>
      </StyledCard>

      <AnimatePresence>
        {isEditing && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
              overflow: "auto",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              style={{
                backgroundColor: "white",
                padding: "2rem",
                borderRadius: "16px",
                width: "90%",
                maxWidth: "700px",
                maxHeight: "90vh",
                overflowY: "auto",
                boxShadow: "0 15px 50px rgba(0, 0, 0, 0.3)",
              }}
            >
              <EdicaoInfo
                dados={dados}
                onSave={handleSave}
                onClose={() => setIsEditing(false)}
              />
            </motion.div>
          </Box>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default EscolaInfo;
